import { useState } from 'react';
import { Switch } from 'antd';
import {
  BugOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  LinkOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { BADGE_DANG_CHO, BAT_TAT, MUC_DO_LOI, TINH_CHAT_LOI } from '../../constants';
import {
  useBoQuaCho,
  useCodeLoi,
  useMucCho,
  useSuaChiTiet,
  useSuaCode,
  useTongQuanCode,
  useXoaChiTiet,
  useXoaCode,
} from '../../hooks/useCauHinhLoi';
import { codeDangXem, tenNganException } from '../../nghiep-vu/loi';
import type { ChiTietLoi, CodeLoi, MucCho } from '../../types';
import { DauKhoi, LoiRpa } from '../chung';
import { HopChiTiet } from './HopChiTiet';
import { HopCode } from './HopCode';
import { HopGanCho } from './HopGanCho';

/** Phân trang phía máy khách chỉ khi danh sách code dài hơn ngưỡng này (như native). */
const MOT_TRANG = 20;

type Hop =
  | { loai: 'code'; goc: CodeLoi | null; cho: MucCho | null }
  | { loai: 'gan'; cho: MucCho }
  | { loai: 'chiTiet'; code: string; goc: ChiTietLoi | null }
  | { loai: 'xoaCode'; code: CodeLoi }
  | { loai: 'goChiTiet'; chiTiet: ChiTietLoi }
  | null;

/**
 * Tab "Cấu hình lỗi" (`ErrorCodesPanel` của native): ba khối xếp dọc — exception Robot báo mà chưa có cách xử lý
 * (Bảng 3), loại lỗi kèm chính sách thử lại (Bảng 1), exception của loại lỗi đang chọn (Bảng 2). Bảng bắt đầu trống:
 * dev bắn exception từ flow OpenRPA, exception hiện ở khối chờ, người dùng tạo loại lỗi từ đó.
 *
 * Thiếu `rpa:cau-hinh-loi:update` thì ẩn mọi nút ghi; công tắc Bật thay bằng nhãn trạng thái.
 */
export function TabCauHinhLoi({ suaDuoc }: { suaDuoc: boolean }) {
  const { t } = useT();
  const tongQuan = useTongQuanCode();
  const [daChon, setDaChon] = useState('');
  const [hop, setHop] = useState<Hop>(null);
  const dong = () => setHop(null);

  if (tongQuan.isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (tongQuan.isError) {
    return (
      <Tile>
        <LoiRpa loi={tongQuan.error} onThuLai={() => void tongQuan.refetch()} />
      </Tile>
    );
  }

  const { codes } = tongQuan.data;
  const dangXem = codeDangXem(codes, daChon);

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed text-content-muted">{t('rpaLoi.gioiThieu')}</p>

      <KhoiCho
        suaDuoc={suaDuoc}
        coCode={codes.length > 0}
        onTao={(cho) => setHop({ loai: 'code', goc: null, cho })}
        onGan={(cho) => setHop({ loai: 'gan', cho })}
      />

      <KhoiCode
        codes={codes}
        dangXem={dangXem?.code ?? ''}
        suaDuoc={suaDuoc}
        onChon={setDaChon}
        onThem={() => setHop({ loai: 'code', goc: null, cho: null })}
        onSua={(code) => setHop({ loai: 'code', goc: code, cho: null })}
        onXoa={(code) => setHop({ loai: 'xoaCode', code })}
      />

      <KhoiChiTiet
        code={dangXem}
        suaDuoc={suaDuoc}
        onThem={() => dangXem && setHop({ loai: 'chiTiet', code: dangXem.code, goc: null })}
        onSua={(ct) => dangXem && setHop({ loai: 'chiTiet', code: dangXem.code, goc: ct })}
        onGo={(ct) => setHop({ loai: 'goChiTiet', chiTiet: ct })}
      />

      {hop?.loai === 'code' && <HopCode goc={hop.goc} cho={hop.cho} onClose={dong} onDaLuu={setDaChon} />}
      {hop?.loai === 'gan' && <HopGanCho cho={hop.cho} codes={codes} onClose={dong} onDaGan={setDaChon} />}
      {hop?.loai === 'chiTiet' && <HopChiTiet code={hop.code} goc={hop.goc} onClose={dong} />}
      {hop?.loai === 'xoaCode' && <HopXoaCode code={hop.code} onClose={dong} />}
      {hop?.loai === 'goChiTiet' && <HopGoChiTiet chiTiet={hop.chiTiet} onClose={dong} />}
    </div>
  );
}

// ─────────────── Bảng 3: exception chờ ───────────────

function KhoiCho({
  suaDuoc,
  coCode,
  onTao,
  onGan,
}: {
  suaDuoc: boolean;
  coCode: boolean;
  onTao: (cho: MucCho) => void;
  onGan: (cho: MucCho) => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const [trangThai, setTrangThai] = useState<'PENDING' | 'IGNORED'>('PENDING');
  const ds = useMucCho(trangThai);
  const boQua = useBoQuaCho();
  const dong = ds.data ?? [];

  const lamBoQua = (cho: MucCho) =>
    boQua.mutate(cho.id, {
      onSuccess: () => toast.success(t('rpaLoi.cho.daBoQua', { exception: cho.exception })),
      onError: (e) => toast.error(dichLoi(e)),
    });

  return (
    <Tile>
      <DauKhoi
        tieuDe={
          <>
            {t('rpaLoi.cho.tieuDe')}
            {trangThai === 'PENDING' && dong.length > 0 && (
              <Badge variant={BADGE_DANG_CHO}>{t('rpaLoi.cho.soDangCho', { so: soVN(dong.length, locale) })}</Badge>
            )}
          </>
        }
        moTa={t('rpaLoi.cho.moTa')}
        hanhDong={
          <Select<'PENDING' | 'IGNORED'>
            value={trangThai}
            onChange={setTrangThai}
            aria-label={t('rpaLoi.cho.locTrangThai')}
            className="w-44"
            options={[
              { value: 'PENDING', label: t('rpaLoi.cho.PENDING') },
              { value: 'IGNORED', label: t('rpaLoi.cho.IGNORED') },
            ]}
          />
        }
      />
      {ds.isPending ? (
        <div className="p-4" aria-busy="true">
          <Skeleton className="h-20 w-full" />
        </div>
      ) : ds.isError ? (
        <LoiRpa loi={ds.error} onThuLai={() => void ds.refetch()} />
      ) : dong.length === 0 ? (
        <Empty
          icon={BugOutlined}
          title={t(trangThai === 'PENDING' ? 'rpaLoi.cho.rong' : 'rpaLoi.cho.rongBoQua')}
          {...(trangThai === 'PENDING' ? { description: t('rpaLoi.cho.rongMoTa') } : {})}
        />
      ) : (
        <ul className="divide-y divide-default" aria-label={t('rpaLoi.cho.danhSach')}>
          {dong.map((cho) => {
            const dangBoQua = boQua.isPending && boQua.variables === cho.id;
            return (
              <li key={cho.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-content-primary">{tenNganException(cho.exception)}</span>
                    <span className="break-all font-mono text-xs text-content-muted">{cho.exception}</span>
                  </p>
                  {cho.sampleMessage && <p className="mt-1 break-words text-xs text-content-secondary">{cho.sampleMessage}</p>}
                  <p className="mt-1 text-xs text-content-muted">
                    {[
                      t('rpaLoi.cho.soLan', { so: soVN(cho.hitCount, locale) }),
                      t('rpaLoi.cho.lanCuoi', { luc: ngayGioVN(cho.lastSeenAt, locale) }),
                      cho.sampleStep ? t('rpaLoi.cho.buoc', { buoc: cho.sampleStep }) : '',
                      cho.sampleWorkflow ? t('rpaLoi.cho.luong', { luong: cho.sampleWorkflow }) : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {suaDuoc && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button variant="primary" size="sm" icon={<PlusOutlined />} onClick={() => onTao(cho)}>
                      {t('rpaLoi.cho.taoCode')}
                    </Button>
                    {/* Có quyền nhưng chưa có loại lỗi nào: nút mờ kèm lý do (ADR-UI-0005), không ẩn. */}
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<LinkOutlined />}
                      disabled={!coCode}
                      title={coCode ? undefined : t('rpaLoi.cho.chuaCoCode')}
                      onClick={() => onGan(cho)}
                    >
                      {t('rpaLoi.cho.gan')}
                    </Button>
                    {trangThai === 'PENDING' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<EyeInvisibleOutlined />}
                        loading={dangBoQua}
                        onClick={() => lamBoQua(cho)}
                      >
                        {dangBoQua ? t('rpaLoi.cho.dangBoQua') : t('rpaLoi.cho.boQua')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Tile>
  );
}

// ─────────────── Bảng 1: loại lỗi ───────────────

function KhoiCode({
  codes,
  dangXem,
  suaDuoc,
  onChon,
  onThem,
  onSua,
  onXoa,
}: {
  codes: CodeLoi[];
  dangXem: string;
  suaDuoc: boolean;
  onChon: (code: string) => void;
  onThem: () => void;
  onSua: (code: CodeLoi) => void;
  onXoa: (code: CodeLoi) => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const sua = useSuaCode();

  const batTat = (c: CodeLoi, bat: boolean) =>
    sua.mutate(
      { code: c.code, than: { enabled: bat } },
      {
        onSuccess: () => toast.success(t(bat ? 'rpaLoi.code.daBat' : 'rpaLoi.code.daTat', { ma: c.code })),
        onError: (e) => toast.error(dichLoi(e)),
      },
    );

  return (
    <Tile className="min-w-0">
      <DauKhoi
        tieuDe={t('rpaLoi.code.tieuDe')}
        moTa={t('rpaLoi.code.moTa')}
        hanhDong={
          suaDuoc ? (
            <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={onThem}>
              {t('rpaLoi.code.them')}
            </Button>
          ) : undefined
        }
      />
      {codes.length === 0 ? (
        <Empty
          icon={BugOutlined}
          title={t('rpaLoi.code.rong')}
          {...(suaDuoc ? { description: t('rpaLoi.code.rongMoTa') } : {})}
        />
      ) : (
        <DataTable<CodeLoi>
          caption={t('rpaLoi.code.tieuDe')}
          dataSource={codes}
          rowKey={(c) => c.code}
          pagination={codes.length > MOT_TRANG ? { defaultPageSize: MOT_TRANG } : false}
          rowClassName={(c) => (c.code === dangXem ? 'cursor-pointer bg-primary-50' : 'cursor-pointer')}
          onRow={(c) => ({ onClick: () => onChon(c.code) })}
          columns={[
            {
              title: t('rpaLoi.code.cotMa'),
              key: 'code',
              width: 80,
              render: (_, c) => <span className="whitespace-nowrap font-mono text-xs font-medium">{c.code}</span>,
            },
            {
              title: t('rpaLoi.code.cotTen'),
              key: 'name',
              render: (_, c) => (
                <div className="min-w-0 max-w-md">
                  <p className="text-sm font-medium text-content-primary">{c.name}</p>
                  {c.description && <p className="line-clamp-2 text-xs text-content-muted">{c.description}</p>}
                  {c.code === dangXem && <span className="sr-only">{t('rpaLoi.code.dangXem')}</span>}
                </div>
              ),
            },
            {
              title: t('rpaLoi.code.cotTinhChat'),
              key: 'foreseen',
              render: (_, c) => {
                const tc = TINH_CHAT_LOI[c.foreseen ? 'luongTruoc' : 'batNgo'];
                return <Badge variant={tc.variant} className="whitespace-nowrap">{t(tc.khoa)}</Badge>;
              },
            },
            {
              title: t('rpaLoi.code.cotMucDo'),
              key: 'severity',
              render: (_, c) => {
                const m = MUC_DO_LOI[c.severity];
                return <Badge variant={m.variant} className="whitespace-nowrap">{t(m.khoa)}</Badge>;
              },
            },
            {
              title: t('rpaLoi.code.cotCachXuLy'),
              key: 'policy',
              render: (_, c) => (
                <div className="max-w-xs text-xs">
                  <p className="text-content-primary">
                    {c.maxRetries > 0
                      ? t('rpaLoi.code.thuLai', { so: soVN(c.maxRetries, locale), giay: soVN(c.retryDelaySeconds, locale) })
                      : t('rpaLoi.code.khongThuLai')}
                  </p>
                  <p className={c.onExhausted === 'STOP' ? 'text-danger-700' : 'text-content-muted'}>
                    {t(c.onExhausted === 'STOP' ? 'rpaLoi.code.hetLanSTOP' : 'rpaLoi.code.hetLanSKIP')}
                  </p>
                </div>
              ),
            },
            {
              title: t('rpaLoi.code.cotException'),
              key: 'detailCount',
              align: 'right',
              width: 100,
              render: (_, c) => <span className="tabular-nums">{soVN(c.detailCount ?? 0, locale)}</span>,
            },
            {
              title: t('rpaLoi.code.cotBat'),
              key: 'enabled',
              width: 90,
              render: (_, c) =>
                suaDuoc ? (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={c.enabled}
                      loading={sua.isPending && sua.variables?.code === c.code}
                      aria-label={t('rpaLoi.code.batNhan', { ma: c.code })}
                      onChange={(bat) => batTat(c, bat)}
                    />
                  </span>
                ) : (
                  <Badge variant={BAT_TAT[c.enabled ? 'bat' : 'tat'].variant} className="whitespace-nowrap">
                    {t(BAT_TAT[c.enabled ? 'bat' : 'tat'].khoa)}
                  </Badge>
                ),
            },
            {
              title: <span className="sr-only">{t('chung.thaoTac')}</span>,
              key: 'thaoTac',
              width: 72,
              align: 'center',
              render: (_, c) => {
                const conChiTiet = (c.detailCount ?? 0) > 0;
                return (
                  <span onClick={(e) => e.stopPropagation()}>
                    <MenuThaoTac
                      tenBanGhi={`${c.code} · ${c.name}`}
                      muc={[
                        { khoa: 'xem', nhan: t('rpaLoi.code.xemException'), icon: <UnorderedListOutlined />, nhom: 'xem',
                          onChon: () => onChon(c.code) },
                        { khoa: 'sua', nhan: t('rpaLoi.code.sua'), icon: <EditOutlined />, hien: suaDuoc, onChon: () => onSua(c) },
                        {
                          khoa: 'xoa',
                          nhan: t('rpaLoi.code.xoa'),
                          icon: <DeleteOutlined />,
                          nhom: 'pha-huy',
                          hien: suaDuoc,
                          // Có quyền nhưng còn exception: hiện mờ kèm lý do (ADR-UI-0005).
                          voHieu: conChiTiet,
                          ...(conChiTiet ? { moTa: t('rpaLoi.code.xoaBiChan') } : {}),
                          onChon: () => onXoa(c),
                        },
                      ]}
                    />
                  </span>
                );
              },
            },
          ]}
        />
      )}
    </Tile>
  );
}

// ─────────────── Bảng 2: exception của loại lỗi đang chọn ───────────────

function KhoiChiTiet({
  code,
  suaDuoc,
  onThem,
  onSua,
  onGo,
}: {
  code: CodeLoi | null;
  suaDuoc: boolean;
  onThem: () => void;
  onSua: (ct: ChiTietLoi) => void;
  onGo: (ct: ChiTietLoi) => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const ct = useCodeLoi(code?.code ?? '');
  const sua = useSuaChiTiet();

  const batTat = (d: ChiTietLoi, bat: boolean) =>
    sua.mutate(
      { id: d.id, than: { enabled: bat } },
      {
        onSuccess: () =>
          toast.success(t(bat ? 'rpaLoi.chiTiet.daBat' : 'rpaLoi.chiTiet.daTat', { exception: tenNganException(d.exception) })),
        onError: (e) => toast.error(dichLoi(e)),
      },
    );

  const tieuDe = code ? t('rpaLoi.chiTiet.tieuDeCua', { ma: code.code, ten: code.name }) : t('rpaLoi.chiTiet.tieuDe');

  return (
    <Tile className="min-w-0">
      <DauKhoi
        tieuDe={tieuDe}
        moTa={t('rpaLoi.chiTiet.moTa')}
        hanhDong={
          code && suaDuoc ? (
            <Button variant="outline" size="md" icon={<PlusOutlined />} onClick={onThem}>
              {t('rpaLoi.chiTiet.them')}
            </Button>
          ) : undefined
        }
      />
      {!code ? (
        <Empty icon={UnorderedListOutlined} title={t('rpaLoi.chiTiet.chuaCoCode')} />
      ) : ct.isPending ? (
        <div className="p-4" aria-busy="true">
          <Skeleton className="h-20 w-full" />
        </div>
      ) : ct.isError ? (
        <LoiRpa loi={ct.error} onThuLai={() => void ct.refetch()} />
      ) : ct.data.details.length === 0 ? (
        <Empty
          icon={UnorderedListOutlined}
          title={t('rpaLoi.chiTiet.rong')}
          {...(suaDuoc ? { description: t('rpaLoi.chiTiet.rongMoTa') } : {})}
        />
      ) : (
        <DataTable<ChiTietLoi>
          caption={tieuDe}
          dataSource={ct.data.details}
          pagination={false}
          columns={[
            {
              title: t('rpaLoi.chiTiet.cotException'),
              key: 'exception',
              render: (_, d) => (
                <div className="min-w-0 max-w-md">
                  <p className="text-sm font-medium text-content-primary">{tenNganException(d.exception)}</p>
                  <p className="break-all font-mono text-xs text-content-muted">{d.exception}</p>
                </div>
              ),
            },
            {
              title: t('rpaLoi.chiTiet.cotThongDiep'),
              key: 'messageContains',
              render: (_, d) =>
                d.messageContains ? (
                  <span className="break-words text-xs">{d.messageContains}</span>
                ) : (
                  <span className="text-xs text-content-muted">{t('rpaLoi.chiTiet.moiThongDiep')}</span>
                ),
            },
            {
              title: t('rpaLoi.chiTiet.cotPhanLoai'),
              key: 'classification',
              render: (_, d) =>
                d.classification ? (
                  <Badge variant="neutral" className="whitespace-nowrap">{d.classification}</Badge>
                ) : (
                  <span className="text-xs text-content-muted">{t('rpaLoi.chiTiet.chuaPhanLoai')}</span>
                ),
            },
            {
              title: t('rpaLoi.chiTiet.cotSoLan'),
              key: 'hitCount',
              align: 'right',
              width: 100,
              render: (_, d) => <span className="tabular-nums">{soVN(d.hitCount, locale)}</span>,
            },
            {
              title: t('rpaLoi.chiTiet.cotLanCuoi'),
              key: 'lastSeenAt',
              width: 150,
              render: (_, d) => (
                <span className="whitespace-nowrap text-xs tabular-nums">{d.lastSeenAt ? ngayGioVN(d.lastSeenAt, locale) : KHONG_CO}</span>
              ),
            },
            {
              title: t('rpaLoi.chiTiet.cotBat'),
              key: 'enabled',
              width: 90,
              render: (_, d) =>
                suaDuoc ? (
                  <Switch
                    checked={d.enabled}
                    loading={sua.isPending && sua.variables?.id === d.id}
                    aria-label={t('rpaLoi.chiTiet.batNhan', { exception: tenNganException(d.exception) })}
                    onChange={(bat) => batTat(d, bat)}
                  />
                ) : (
                  <Badge variant={BAT_TAT[d.enabled ? 'bat' : 'tat'].variant} className="whitespace-nowrap">
                    {t(BAT_TAT[d.enabled ? 'bat' : 'tat'].khoa)}
                  </Badge>
                ),
            },
            ...(suaDuoc
              ? [
                  {
                    title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                    key: 'thaoTac',
                    width: 72,
                    align: 'center' as const,
                    render: (_: unknown, d: ChiTietLoi) => (
                      <MenuThaoTac
                        tenBanGhi={d.exception}
                        muc={[
                          { khoa: 'sua', nhan: t('rpaLoi.chiTiet.sua'), icon: <EditOutlined />, onChon: () => onSua(d) },
                          { khoa: 'go', nhan: t('rpaLoi.chiTiet.go'), icon: <DeleteOutlined />, nhom: 'pha-huy' as const,
                            onChon: () => onGo(d) },
                        ]}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}
    </Tile>
  );
}

// ─────────────── Hộp xác nhận ───────────────

function HopXoaCode({ code, onClose }: { code: CodeLoi; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const xoa = useXoaCode();
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaLoi.code.xoaTieuDe', { ma: code.code })}
      than={t('rpaLoi.code.xoaThan', { ma: code.code, ten: code.name })}
      nhanXacNhan={t('rpaLoi.code.xoa')}
      dangChay={xoa.isPending}
      loi={xoa.error ? dichLoi(xoa.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        xoa.mutate(code.code, {
          onSuccess: () => {
            toast.success(t('rpaLoi.code.daXoa', { ma: code.code }));
            onClose();
          },
        })
      }
    />
  );
}

function HopGoChiTiet({ chiTiet, onClose }: { chiTiet: ChiTietLoi; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const go = useXoaChiTiet();
  const ten = tenNganException(chiTiet.exception);
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaLoi.chiTiet.goTieuDe', { exception: ten })}
      than={t('rpaLoi.chiTiet.goThan', { exception: chiTiet.exception })}
      nhanXacNhan={t('rpaLoi.chiTiet.go')}
      dangChay={go.isPending}
      loi={go.error ? dichLoi(go.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        go.mutate(chiTiet.id, {
          onSuccess: () => {
            toast.success(t('rpaLoi.chiTiet.daGo', { exception: ten }));
            onClose();
          },
        })
      }
    />
  );
}
